import React, { useState, useEffect, useLayoutEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Avatar,
  Divider,
  TablePagination,
  InputAdornment,
  Tab,
  Tabs,
  Badge,
  LinearProgress,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  FormHelperText,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  MobileStepper,
  CardActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  DirectionsCar as CarIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  LocalShipping as TransportIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon,
  Today as TodayIcon,
  Weekend as WeekendIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Notes as NotesIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import LoadingBackDrop from "../global/LoadingBackDrop";
import { getPayload, setPostPayload, setPutPayload } from "../../services/api";

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  marginBottom: theme.spacing(3),
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  backgroundColor:
    status === "confirmed"
      ? "#e8f5e9"
      : status === "pending"
        ? "#fff3e0"
        : status === "completed"
          ? "#e3f2fd"
          : status === "cancelled"
            ? "#ffebee"
            : "#f5f5f5",
  color:
    status === "confirmed"
      ? "#2e7d32"
      : status === "pending"
        ? "#e65100"
        : status === "completed"
          ? "#1565c0"
          : status === "cancelled"
            ? "#c62828"
            : "#616161",
  "& .MuiChip-icon": {
    color:
      status === "confirmed"
        ? "#2e7d32"
        : status === "pending"
          ? "#e65100"
          : status === "completed"
            ? "#1565c0"
            : status === "cancelled"
              ? "#c62828"
              : "#616161",
  },
}));

const VehicleCard = styled(Card)(({ theme, available }) => ({
  height: "100%",
  transition: "all 0.3s ease",
  cursor: available ? "pointer" : "default",
  opacity: available ? 1 : 0.6,
  border: available ? "2px solid transparent" : "2px solid #ffcdd2",
  "&:hover": {
    transform: available ? "translateY(-4px)" : "none",
    boxShadow: available ? "0 8px 24px rgba(0,0,0,0.12)" : "none",
  },
}));

const VehicleBooking = ({ loggedUser }) => {
  // State variables
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [openBookingDialog, setOpenBookingDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [filterStatus, setFilterStatus] = useState("all");

  // Form state
  const [formData, setFormData] = useState({
    jobId: "",
    vehicleId: "",
    bookingDate: "",
    startTime: "",
    endTime: "",
    purpose: "",
    destination: "",
    contactPerson: "",
    contactNumber: "",
    notes: "",
    status: "pending",
    isRecurring: false,
    recurringDays: [],
  });

  // Vehicle types
  const vehicleTypes = [
    "Car",
    "Van",
    "Truck",
    "Bus",
    "Motorcycle",
    "SUV",
    "Pickup",
    "Mini Truck",
    "Three Wheeler",
    "Other",
  ];

  // Load data on mount
  useLayoutEffect(() => {
    loadVehicles();
    loadBookings();
    loadJobs();
  }, []);

  // Load vehicles from API
  const loadVehicles = async () => {
    setLoading(true);
    try {
      const response = await getPayload.getPayloadData("/vehicle/all");
      const allVehicles = response.data || [];
      setVehicles(allVehicles);

      // Filter available vehicles (active and not currently booked)
      const available = allVehicles.filter(
        (v) => v.status === "active" && !isVehicleBooked(v.id),
      );
      setAvailableVehicles(available);
    } catch (error) {
      console.error("Error loading vehicles:", error);
      setSnackbar({
        open: true,
        message: "Failed to load vehicles",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Load bookings from API
  const loadBookings = async () => {
    try {
      const response = await getPayload.getPayloadData("/vehicle/booking/all");
      setBookings(response.data || []);
    } catch (error) {
      console.error("Error loading bookings:", error);
    }
  };

  // Load jobs from API
  const loadJobs = async () => {
    try {
      // Assuming you have a job endpoint
      const response = await getPayload.getPayloadData("/job/all");
      setJobs(response.data || []);
    } catch (error) {
      console.error("Error loading jobs:", error);
      // Set default jobs if API fails
      setJobs([
        {
          id: 1,
          jobNumber: "JOB/2024/001",
          project: "Sample Collection Project A",
        },
        {
          id: 2,
          jobNumber: "JOB/2024/002",
          project: "Field Testing Project B",
        },
        { id: 3, jobNumber: "JOB/2024/003", project: "Research Project C" },
      ]);
    }
  };

  // Check if vehicle is currently booked
  const isVehicleBooked = (vehicleId, excludeBookingId = null) => {
    const now = new Date();
    return bookings.some(
      (booking) =>
        booking.vehicleId === vehicleId &&
        booking.status !== "cancelled" &&
        booking.status !== "completed" &&
        (excludeBookingId === null || booking.id !== excludeBookingId) &&
        new Date(booking.bookingDate) >= new Date(),
    );
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Open booking dialog
  const handleBookVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsEditMode(false);
    setFormData({
      jobId: "",
      vehicleId: vehicle.id,
      bookingDate: new Date().toISOString().split("T")[0],
      startTime: "09:00",
      endTime: "17:00",
      purpose: "",
      destination: "",
      contactPerson: loggedUser?.firstName || "",
      contactNumber: loggedUser?.contactNo || "",
      notes: "",
      status: "pending",
      isRecurring: false,
      recurringDays: [],
    });
    setOpenBookingDialog(true);
  };

  // Open view/edit booking dialog
  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setIsEditMode(
      booking.status === "pending" || booking.status === "confirmed",
    );
    setFormData({
      jobId: booking.jobId || "",
      vehicleId: booking.vehicleId || "",
      bookingDate: booking.bookingDate || "",
      startTime: booking.startTime || "09:00",
      endTime: booking.endTime || "17:00",
      purpose: booking.purpose || "",
      destination: booking.destination || "",
      contactPerson: booking.contactPerson || "",
      contactNumber: booking.contactNumber || "",
      notes: booking.notes || "",
      status: booking.status || "pending",
      isRecurring: booking.isRecurring || false,
      recurringDays: booking.recurringDays || [],
    });
    setOpenViewDialog(true);
  };

  // Save booking
  const handleSaveBooking = async () => {
    // Validation
    if (!formData.jobId || !formData.vehicleId || !formData.bookingDate) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      let response;
      if (isEditMode && selectedBooking) {
        // Update existing booking
        response = await setPutPayload.setPayloadData(
          `/vehicle/booking/update/${selectedBooking.id}`,
          formData,
        );
      } else {
        // Add new booking
        response = await setPostPayload.setPayloadData(
          "/vehicle/booking/save",
          formData,
        );
      }

      setSnackbar({
        open: true,
        message: isEditMode
          ? "Booking updated successfully!"
          : "Vehicle booked successfully!",
        severity: "success",
      });
      setOpenBookingDialog(false);
      setOpenViewDialog(false);
      loadBookings();
      loadVehicles(); // Refresh vehicle availability
    } catch (error) {
      console.error("Error saving booking:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to save booking",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Cancel booking
  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) {
      return;
    }

    setLoading(true);
    try {
      await setPutPayload.setPayloadData(
        `/vehicle/booking/cancel/${bookingId}`,
        { status: "cancelled" },
      );

      setSnackbar({
        open: true,
        message: "Booking cancelled successfully!",
        severity: "success",
      });
      loadBookings();
      loadVehicles();
    } catch (error) {
      console.error("Error cancelling booking:", error);
      setSnackbar({
        open: true,
        message: "Failed to cancel booking",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Complete booking
  const handleCompleteBooking = async (bookingId) => {
    if (!window.confirm("Mark this booking as completed?")) {
      return;
    }

    setLoading(true);
    try {
      await setPutPayload.setPayloadData(
        `/vehicle/booking/complete/${bookingId}`,
        { status: "completed" },
      );

      setSnackbar({
        open: true,
        message: "Booking marked as completed!",
        severity: "success",
      });
      loadBookings();
      loadVehicles();
    } catch (error) {
      console.error("Error completing booking:", error);
      setSnackbar({
        open: true,
        message: "Failed to complete booking",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get booking status color
  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "success";
      case "pending":
        return "warning";
      case "completed":
        return "info";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  // Filter bookings based on search and status
  const filteredBookings = bookings.filter((booking) => {
    const vehicle = vehicles.find((v) => v.id === booking.vehicleId);
    const job = jobs.find((j) => j.id === booking.jobId);

    const matchesSearch =
      vehicle?.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle?.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job?.jobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.purpose?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.destination?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" || booking.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Filter bookings by tab
  const getTabBookings = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (tabValue) {
      case 0: // Upcoming
        return filteredBookings.filter(
          (b) =>
            new Date(b.bookingDate) >= today &&
            b.status !== "completed" &&
            b.status !== "cancelled",
        );
      case 1: // Today
        return filteredBookings.filter(
          (b) =>
            new Date(b.bookingDate).toDateString() === today.toDateString() &&
            b.status !== "completed" &&
            b.status !== "cancelled",
        );
      case 2: // Past
        return filteredBookings.filter(
          (b) =>
            new Date(b.bookingDate) < today ||
            b.status === "completed" ||
            b.status === "cancelled",
        );
      default:
        return filteredBookings;
    }
  };

  // Pagination
  const paginatedBookings = getTabBookings().slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    try {
      const [hours, minutes] = timeString.split(":");
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes));
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timeString;
    }
  };

  // Get vehicle by ID
  const getVehicleById = (id) => {
    return vehicles.find((v) => v.id === id);
  };

  // Get job by ID
  const getJobById = (id) => {
    return jobs.find((j) => j.id === id);
  };

  // Check if vehicle is available for selected date
  const isVehicleAvailableForDate = (
    vehicleId,
    date,
    excludeBookingId = null,
  ) => {
    const bookingDate = new Date(date);
    return !bookings.some(
      (booking) =>
        booking.vehicleId === vehicleId &&
        booking.status !== "cancelled" &&
        booking.status !== "completed" &&
        (excludeBookingId === null || booking.id !== excludeBookingId) &&
        new Date(booking.bookingDate).toDateString() ===
          bookingDate.toDateString(),
    );
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ py: 4, bgcolor: "#f5f7fa", minHeight: "100vh" }}
    >
      {loading && <LoadingBackDrop openBackDrop={loading} />}

      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "#1a237e",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <TransportIcon sx={{ fontSize: 40, color: "#1976d2" }} />
              Vehicle Booking
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Book vehicles for laboratory operations and field visits
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Chip
              icon={<EventAvailableIcon />}
              label={`${availableVehicles.length} Available`}
              color="success"
              sx={{ fontSize: "1rem", p: 2 }}
            />
            <Chip
              icon={<EventBusyIcon />}
              label={`${vehicles.length - availableVehicles.length} Booked`}
              color="error"
              sx={{ fontSize: "1rem", p: 2 }}
            />
          </Box>
        </Box>
      </Box>

      {/* Available Vehicles Section */}
      <StyledPaper>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Available Vehicles
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Select a vehicle to book for your job
        </Typography>

        <Grid container spacing={3}>
          {availableVehicles.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">
                No vehicles are currently available. Please check back later.
              </Alert>
            </Grid>
          ) : (
            availableVehicles.map((vehicle) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={vehicle.id}>
                <VehicleCard available={true}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar
                        sx={{ bgcolor: "#1976d2", width: 56, height: 56 }}
                      >
                        <CarIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {vehicle.number}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {vehicle.model}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          Type
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {vehicle.vehicleType}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          Year
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {vehicle.year}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          Fuel
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {vehicle.fuelType}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          Driver
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {vehicle.assignedDriver || "Not Assigned"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleBookVehicle(vehicle)}
                      sx={{ bgcolor: "#1976d2" }}
                    >
                      Book Vehicle
                    </Button>
                  </CardActions>
                </VehicleCard>
              </Grid>
            ))
          )}
        </Grid>
      </StyledPaper>

      {/* Bookings Section */}
      <StyledPaper>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          <Box>
            <Typography variant="h5" fontWeight={600}>
              My Bookings
            </Typography>
            <Typography variant="body2" color="textSecondary">
              View and manage your vehicle bookings
            </Typography>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search bookings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadBookings();
                loadVehicles();
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Upcoming" />
          <Tab label="Today" />
          <Tab label="Past" />
        </Tabs>

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: "#f5f7fa" }}>
              <TableRow>
                <TableCell>
                  <strong>#</strong>
                </TableCell>
                <TableCell>
                  <strong>Job</strong>
                </TableCell>
                <TableCell>
                  <strong>Vehicle</strong>
                </TableCell>
                <TableCell>
                  <strong>Date</strong>
                </TableCell>
                <TableCell>
                  <strong>Time</strong>
                </TableCell>
                <TableCell>
                  <strong>Purpose</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <TransportIcon
                        sx={{ fontSize: 60, color: "#ccc", mb: 2 }}
                      />
                      <Typography variant="h6" color="textSecondary">
                        No bookings found
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {searchTerm || filterStatus !== "all"
                          ? "Try adjusting your search or filters"
                          : "Book a vehicle to get started"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedBookings.map((booking, index) => {
                  const vehicle = getVehicleById(booking.vehicleId);
                  const job = getJobById(booking.jobId);
                  const isEditable =
                    booking.status === "pending" ||
                    booking.status === "confirmed";

                  return (
                    <TableRow key={booking.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {job?.jobNumber || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {job?.project?.substring(0, 30) || ""}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CarIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight={500}>
                            {vehicle?.number || "N/A"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarIcon fontSize="small" color="action" />
                          {formatDate(booking.bookingDate)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccessTimeIcon fontSize="small" color="action" />
                          {formatTime(booking.startTime)} -{" "}
                          {formatTime(booking.endTime)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={booking.purpose || ""}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {booking.purpose || "-"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          icon={
                            booking.status === "confirmed" ? (
                              <CheckCircleIcon />
                            ) : booking.status === "pending" ? (
                              <AccessTimeIcon />
                            ) : booking.status === "completed" ? (
                              <CheckCircleIcon />
                            ) : booking.status === "cancelled" ? (
                              <CancelIcon />
                            ) : (
                              <AssignmentIcon />
                            )
                          }
                          label={
                            booking.status.charAt(0).toUpperCase() +
                            booking.status.slice(1)
                          }
                          status={booking.status}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleViewBooking(booking)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isEditable && (
                            <>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewBooking(booking)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Complete">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() =>
                                    handleCompleteBooking(booking.id)
                                  }
                                >
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleCancelBooking(booking.id)
                                  }
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={getTabBookings().length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </StyledPaper>

      {/* Booking Dialog */}
      <Dialog
        open={openBookingDialog}
        onClose={() => setOpenBookingDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{ bgcolor: "#f5f7fa", borderBottom: "1px solid #e0e0e0" }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: "#1976d2" }}>
              <TransportIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Book Vehicle
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedVehicle?.number} - {selectedVehicle?.model}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Job Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Select Job</InputLabel>
                <Select
                  name="jobId"
                  value={formData.jobId}
                  onChange={handleInputChange}
                  label="Select Job"
                >
                  {jobs.map((job) => (
                    <MenuItem key={job.id} value={job.id}>
                      {job.jobNumber} - {job.project?.substring(0, 50)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Vehicle Info */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vehicle"
                value={`${selectedVehicle?.number} - ${selectedVehicle?.model}`}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CarIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Driver"
                value={selectedVehicle?.assignedDriver || "Not Assigned"}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Date and Time */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Booking Date"
                name="bookingDate"
                type="date"
                value={formData.bookingDate}
                onChange={handleInputChange}
                required
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Start Time"
                name="startTime"
                type="time"
                value={formData.startTime}
                onChange={handleInputChange}
                required
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="End Time"
                name="endTime"
                type="time"
                value={formData.endTime}
                onChange={handleInputChange}
                required
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Purpose and Destination */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                placeholder="Purpose of the trip"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <NotesIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination"
                name="destination"
                value={formData.destination}
                onChange={handleInputChange}
                placeholder="Where are you going?"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Person"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
                placeholder="Name of contact person"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Number"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleInputChange}
                placeholder="Contact number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes or requirements"
                multiline
                rows={2}
              />
            </Grid>

            {/* Recurring Booking Option */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f5f7fa" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isRecurring}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          isRecurring: e.target.checked,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label="Recurring Booking"
                />
                {formData.isRecurring && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      gutterBottom
                    >
                      Select days for recurring booking:
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {[
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                        "Sunday",
                      ].map((day) => (
                        <Chip
                          key={day}
                          label={day}
                          clickable
                          color={
                            formData.recurringDays?.includes(day)
                              ? "primary"
                              : "default"
                          }
                          onClick={() => {
                            const days = formData.recurringDays || [];
                            const newDays = days.includes(day)
                              ? days.filter((d) => d !== day)
                              : [...days, day];
                            setFormData((prev) => ({
                              ...prev,
                              recurringDays: newDays,
                            }));
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
          <Button
            onClick={() => setOpenBookingDialog(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveBooking}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            Book Vehicle
          </Button>
        </DialogActions>
      </Dialog>

      {/* View/Edit Booking Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{ bgcolor: "#f5f7fa", borderBottom: "1px solid #e0e0e0" }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: "#1976d2" }}>
                <VisibilityIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Booking Details
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedBooking?.id && `Booking #${selectedBooking.id}`}
                </Typography>
              </Box>
            </Box>
            <Chip
              icon={
                selectedBooking?.status === "confirmed" ? (
                  <CheckCircleIcon />
                ) : (
                  <AccessTimeIcon />
                )
              }
              label={
                selectedBooking?.status?.charAt(0).toUpperCase() +
                selectedBooking?.status?.slice(1)
              }
              color={getStatusColor(selectedBooking?.status)}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Job and Vehicle Info */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafbfe" }}>
                <Typography variant="caption" color="textSecondary">
                  Job Details
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {getJobById(formData.jobId)?.jobNumber || "N/A"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {getJobById(formData.jobId)?.project || ""}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafbfe" }}>
                <Typography variant="caption" color="textSecondary">
                  Vehicle Details
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {getVehicleById(formData.vehicleId)?.number || "N/A"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {getVehicleById(formData.vehicleId)?.model || ""}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Driver:{" "}
                  {getVehicleById(formData.vehicleId)?.assignedDriver ||
                    "Not Assigned"}
                </Typography>
              </Paper>
            </Grid>

            {/* Date and Time */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Booking Date"
                name="bookingDate"
                type="date"
                value={formData.bookingDate}
                onChange={handleInputChange}
                disabled={!isEditMode}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Start Time"
                name="startTime"
                type="time"
                value={formData.startTime}
                onChange={handleInputChange}
                disabled={!isEditMode}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="End Time"
                name="endTime"
                type="time"
                value={formData.endTime}
                onChange={handleInputChange}
                disabled={!isEditMode}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Purpose and Destination */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                disabled={!isEditMode}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination"
                name="destination"
                value={formData.destination}
                onChange={handleInputChange}
                disabled={!isEditMode}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Person"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
                disabled={!isEditMode}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Number"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleInputChange}
                disabled={!isEditMode}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Notes and Status */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={!isEditMode}
                multiline
                rows={2}
              />
            </Grid>

            {!isEditMode && (
              <Grid item xs={12}>
                <Alert severity="info">
                  This booking is {selectedBooking?.status} and cannot be
                  edited.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={() => setOpenViewDialog(false)} variant="outlined">
            Close
          </Button>
          {isEditMode && (
            <Button
              onClick={handleSaveBooking}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              Update Booking
            </Button>
          )}
          {selectedBooking?.status === "pending" && (
            <Button
              onClick={() => handleCancelBooking(selectedBooking.id)}
              variant="contained"
              color="error"
              startIcon={<CancelIcon />}
              disabled={loading}
            >
              Cancel Booking
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VehicleBooking;
